// Web-exclusive Job Details screen for doctors
// Displays job details with apply functionality and status handling

import React, { useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, TextInput, Linking } from 'react-native';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigation, useRoute } from '@react-navigation/native';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import { Clock, MapPin, DollarSign, Star, Phone, User, Navigation, Check, AlertTriangle, Calendar } from 'lucide-react';
import WebLayout from '../../components/web/WebLayout';
import WebModal from '../../components/web/WebModal';
import { webColors, webSpacing, webBorderRadius, webShadows } from '../../constants/webStyles';
import { getJob, getJobFacilities, applyJob, approveJob, rejectJob, deleteJob, acceptCancellation, getCancellationList } from '../../services/job';
import { getOverview } from '../../services/profile';
import { addFavoriteJob, removeFavoriteJob } from '../../reducers/jobs/favoriteJobs';
import { jobTypes, isFullTimeJob } from '../../constants/lov/professions';
import { formatPrice, formatPriceRange, getJobDisplayCurrency } from '../../utils/currency';
import { useIsMobile } from '../../hooks/useIsMobile';

const DetailsScreen = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();

  const { isDoctor, country: userCountry, currencyPreset } = useSelector((state) => state.user);
  const { favorites } = useSelector((state) => state.favoriteJobs);

  // Get job data from route params
  const currentJob = route.params?.data;
  const fromJobTab = route.params?.fromJobTab;
  const jobId = route.params?.jobId || currentJob?.uuid;

  const [showApplyModal, setShowApplyModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [applySuccess, setApplySuccess] = useState(false);

  // Fetch job details
  const { data: jobQuery, isLoading, refetch: refetchJob } = useQuery({
    queryKey: ['job', jobId],
    queryFn: () => getJob(jobId),
    enabled: !!jobId,
  });
  const job = jobQuery?.data?.data || jobQuery?.data;

  // Fetch job facilities/clinical expertise
  const { data: jobFacilitiesQuery, refetch: refetchFacilities } = useQuery({
    queryKey: ['jobFacilities', jobId],
    queryFn: () => getJobFacilities(jobId),
    enabled: !!jobId,
  });
  const jobFacilityData = jobFacilitiesQuery?.data?.data;

  // Fetch profile for approval check
  const { data: profileQuery } = useQuery({
    queryKey: ['overview'],
    queryFn: getOverview,
  });
  const profileInfo = profileQuery?.data?.data;

  // Fetch cancellation list if status is -3
  const { data: cancelQuery } = useQuery({
    queryKey: ['cancellationList'],
    queryFn: getCancellationList,
    enabled: currentJob?.status === -3,
  });

  const isFullTime = isFullTimeJob(job?.type);
  const displayCurrency = getJobDisplayCurrency(job ?? currentJob, currentJob?.country || userCountry || currencyPreset);

  // Calculate hours difference
  const diffHours = Math.abs(
    moment(job?.start_timestamp).diff(moment(job?.end_timestamp), 'hours') || 1
  );

  // Apply mutation
  const applyMutation = useMutation({
    mutationFn: currentJob?.status === 1 ? approveJob : applyJob,
    onSuccess: () => {
      setApplySuccess(true);
      queryClient.invalidateQueries(['job', jobId]);
      queryClient.invalidateQueries(['jobs']);
    },
    onError: (error) => {
      setShowApplyModal(false);
      setTimeout(() => {
        alert('Oops! ' + (error.response?.data?.message || 'Something went wrong'));
      }, 400);
    },
  });

  // Reject mutation
  const rejectMutation = useMutation({
    mutationFn: rejectJob,
    onSuccess: () => {
      navigation.navigate('Home');
    },
    onError: (error) => {
      alert('Oops! ' + (error.response?.data?.message || 'Something went wrong'));
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: deleteJob,
    onSuccess: () => {
      navigation.navigate('HomeScreen');
    },
    onError: (error) => {
      alert('Oops! ' + (error.response?.data?.message || 'Something went wrong'));
    },
  });

  const handleApply = () => {
    applyMutation.mutate({
      uuid: currentJob?.status === 1 ? currentJob?.job_application?.uuid : job?.uuid,
    });
  };

  const handleReject = () => {
    if (window.confirm('Are you sure you want to reject this job?')) {
      rejectMutation.mutate({
        uuid: currentJob?.job_application?.uuid,
      });
    }
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this job?')) {
      deleteMutation.mutate(currentJob?.uuid);
    }
  };

  const toggleFavorite = () => {
    if (favorites.includes(job?.uuid)) {
      dispatch(removeFavoriteJob(job?.uuid));
    } else {
      dispatch(addFavoriteJob(job?.uuid));
    }
  };

  const isFavorited = favorites.includes(job?.uuid);
  const isRecentlyApproved = currentJob?.status === 3 && isDoctor;

  if (isLoading) {
    return (
      <WebLayout>
        <View style={styles.loadingContainer}>
          <Text style={styles.loadingText}>Loading job details...</Text>
        </View>
      </WebLayout>
    );
  }

  if (!job) {
    return (
      <WebLayout>
        <View style={styles.errorContainer}>
          <Text style={styles.errorIcon}>404</Text>
          <Text style={styles.errorTitle}>Job not found</Text>
          <Text style={styles.errorText}>This job may have been removed or the link is invalid.</Text>
          <TouchableOpacity style={styles.backBtn} onPress={() => navigation.navigate('Jobs')}>
            <Text style={styles.backBtnText}>Browse All Jobs</Text>
          </TouchableOpacity>
        </View>
      </WebLayout>
    );
  }

  const getStatusLabel = () => {
    const status = currentJob?.status;
    if (status === undefined || status === null) return { text: 'Apply Now', color: webColors.primary };
    if (status === 1) return { text: 'Accept Job', color: '#22c55e' };
    if (status === 2) return { text: 'Pending Approval', color: '#f59e0b' };
    if (status === 3) return { text: 'Approved', color: '#22c55e' };
    if (status === 4) return { text: 'Completed', color: '#3b82f6' };
    if (status === -1) return { text: 'Cancelled', color: '#ef4444' };
    if (status === -3) return { text: 'Pending Cancellation', color: '#f59e0b' };
    if (status === 0 && isFullTime) return { text: 'Applied', color: '#22c55e' };
    if (status === 0) return { text: 'Pending', color: '#f59e0b' };
    return { text: 'Apply Now', color: webColors.primary };
  };

  const statusInfo = getStatusLabel();
  const canApply = currentJob?.status === undefined || currentJob?.status === null || currentJob?.status === 1;

  return (
    <WebLayout>
      <ScrollView style={styles.scrollContainer} showsVerticalScrollIndicator={false}>
        {/* Breadcrumb */}
        <View style={styles.breadcrumb}>
          <TouchableOpacity onPress={() => navigation.navigate('Jobs')}>
            <Text style={styles.breadcrumbLink}>Jobs</Text>
          </TouchableOpacity>
          <Text style={styles.breadcrumbSeparator}>/</Text>
          <Text style={styles.breadcrumbCurrent}>{job.title}</Text>
        </View>

        <View style={[styles.contentLayout, isMobile && styles.contentLayoutMobile]}>
          {/* Main Content */}
          <View style={styles.mainContent}>
            {/* Job Header Card */}
            <View style={styles.jobHeaderCard}>
              <View style={styles.jobHeaderTop}>
                <View style={[styles.companyLogo, { backgroundColor: getColorFromName(job.clinic?.name) }]}>
                  <Text style={styles.companyLogoText}>{job.clinic?.name?.[0] || 'C'}</Text>
                </View>
                <View style={styles.jobHeaderInfo}>
                  {job.is_emergency && (
                    <Text style={styles.urgentLabel}>URGENT JOB</Text>
                  )}
                  <Text style={styles.companyName}>{job.clinic?.name || 'Healthcare Facility'}</Text>
                  <Text style={[styles.jobTitle, isMobile && styles.jobTitleMobile]}>{job.title}</Text>
                  <View style={styles.jobMeta}>
                    <View style={styles.metaItem}>
                      <Clock size={14} color={webColors.textMuted} />
                      <Text style={styles.metaText}>Posted {moment(job.created_at).fromNow()}</Text>
                    </View>
                    <View style={styles.metaItem}>
                      <MapPin size={14} color={webColors.textMuted} />
                      <Text style={styles.metaText}>{job.clinic?.city || job.location || 'Location TBD'}</Text>
                    </View>
                    {!isFullTime && (
                      <View style={styles.metaItem}>
                        <DollarSign size={14} color={webColors.textMuted} />
                        <Text style={styles.metaText}>{formatPrice(job.price, displayCurrency)}</Text>
                      </View>
                    )}
                  </View>
                </View>
              </View>

              {/* Applied badge for non-locum jobs */}
              {job?.job_application && currentJob?.status === 0 && isFullTime && (
                <View style={styles.headerActions}>
                  <View style={[styles.statusBadge, { backgroundColor: '#22c55e20' }]}>
                    <Text style={[styles.statusBadgeText, { color: '#22c55e' }]}>Applied</Text>
                  </View>
                </View>
              )}

              {/* Action Buttons - hidden when user has already applied */}
              {!job?.job_application && (
                <View style={styles.headerActions}>
                  {canApply ? (
                    <TouchableOpacity
                      style={[styles.applyBtn, { backgroundColor: statusInfo.color }]}
                      onPress={() => {
                        if (profileInfo?.is_approved) {
                          setShowApplyModal(true);
                        } else {
                          alert('Please complete your profile to apply for jobs.');
                          navigation.navigate('ProfileScreen');
                        }
                      }}
                    >
                      <Text style={styles.applyBtnText}>{statusInfo.text}</Text>
                    </TouchableOpacity>
                  ) : (
                    <View style={[styles.statusBadge, { backgroundColor: statusInfo.color + '20' }]}>
                      <Text style={[styles.statusBadgeText, { color: statusInfo.color }]}>{statusInfo.text}</Text>
                    </View>
                  )}
                  <TouchableOpacity style={styles.saveBtn} onPress={toggleFavorite}>
                    <View style={styles.saveBtnContent}>
                      <Star size={14} color={isFavorited ? '#f59e0b' : webColors.textSecondary} fill={isFavorited ? '#f59e0b' : 'none'} />
                      <Text style={styles.saveBtnText}>{isFavorited ? 'Saved' : 'Save'}</Text>
                    </View>
                  </TouchableOpacity>
                </View>
              )}

              {/* Tags */}
              <View style={styles.jobTags}>
                <View style={styles.jobTypeTag}>
                  <Text style={styles.jobTypeTagText}>
                    {job.international ? 'International' : jobTypes.find(type => type.id === parseInt(job.type))?.name || 'Locum'}
                  </Text>
                </View>
                {!job.is_cash && (
                  <View style={styles.spayTag}>
                    <Text style={styles.spayTagText}>S-PAY</Text>
                  </View>
                )}
                {job.is_cash && (
                  <View style={styles.cashTag}>
                    <Text style={styles.cashTagText}>CASH</Text>
                  </View>
                )}
              </View>
            </View>

            {/* Description Section */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>About the job</Text>
              <Text style={styles.descriptionText}>
                {job.description || 'No description provided.'}
              </Text>
            </View>

            {/* Salary/Earnings Section */}
            {isFullTime ? (
              job.price > 0 && (
                <View style={styles.section}>
                  <Text style={styles.sectionTitle}>Job Salary</Text>
                  <Text style={styles.salaryText}>
                    {formatPriceRange(job.price, job.price_max, displayCurrency)} per month
                  </Text>
                </View>
              )
            ) : (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Earnings</Text>
                <Text style={styles.earningsHighlight}>
                  {formatPrice((job.price / diffHours).toFixed(2), displayCurrency)} per hour
                </Text>
                <Text style={styles.earningsTotal}>
                  Total: {formatPrice(job.price, displayCurrency)} ({diffHours} hours)
                </Text>
                <View style={[styles.paymentTag, { borderColor: !job.is_cash ? '#FC6767' : 'grey' }]}>
                  <Text style={[styles.paymentTagText, { color: !job.is_cash ? '#FC6767' : 'grey' }]}>
                    {job.is_cash ? 'CASH' : 'S-PAY'}
                  </Text>
                </View>
                <Text style={styles.paymentNote}>
                  {job.is_cash
                    ? 'Total shift pay will be made to you by the facility, you are required to liaise directly with the respective facility. T&C apply.'
                    : "Total shift pay will be credited directly into your bank account from Supermedic. You're eligible for the following: Next-day pay | Immediate shift coverage. T&C apply."}
                </Text>
              </View>
            )}

            {/* Clinical Expertise Section (for locum jobs) */}
            {!isFullTime && jobFacilityData?.length > 0 && (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Clinical Expertise Required</Text>

                {/* GP */}
                {jobFacilityData.filter(val => val.category === 'GP').length > 0 && (
                  <View style={styles.expertiseGroup}>
                    <Text style={styles.expertiseLabel}>GENERAL PRACTITIONER</Text>
                    {jobFacilityData.filter(val => val.category === 'GP').map((speciality, index) => (
                      <View key={index} style={styles.listItem}>
                        <Text style={styles.listBullet}>•</Text>
                        <Text style={styles.listText}>{speciality.doctor_title}</Text>
                      </View>
                    ))}
                  </View>
                )}

                {/* Specialists */}
                {jobFacilityData.filter(val => val.category !== 'GP').length > 0 && (
                  <View style={styles.expertiseGroup}>
                    <Text style={styles.expertiseLabel}>SPECIALIST</Text>
                    {jobFacilityData.filter(val => val.category !== 'GP').map((speciality, index) => (
                      <View key={index} style={styles.listItem}>
                        <Text style={styles.listBullet}>•</Text>
                        <Text style={styles.listText}>{speciality.doctor_title}</Text>
                      </View>
                    ))}
                  </View>
                )}
              </View>
            )}

            {/* Date & Time Section */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>{isFullTime ? 'Application Deadline' : 'Date & Time'}</Text>
              {moment(job.start_timestamp).format('DD') !== moment(job.end_timestamp).format('DD') ? (
                <View style={styles.dateRange}>
                  <View style={styles.dateItem}>
                    <Text style={styles.dateMain}>{moment(job.start_timestamp).format('MMMM Do')}</Text>
                    {!isFullTime && (
                      <Text style={styles.dateTime}>{moment(job.start_timestamp).format('h:mm A')}</Text>
                    )}
                  </View>
                  <Text style={styles.dateSeparator}>~</Text>
                  <View style={styles.dateItem}>
                    <Text style={styles.dateMain}>{moment(job.end_timestamp).format('MMMM Do')}</Text>
                    {!isFullTime && (
                      <Text style={styles.dateTime}>{moment(job.end_timestamp).format('h:mm A')}</Text>
                    )}
                  </View>
                </View>
              ) : (
                <View>
                  <Text style={styles.dateMain}>{moment(job.start_timestamp).format('dddd, MMMM Do YYYY')}</Text>
                  {!isFullTime && (
                    <Text style={styles.dateTime}>
                      {moment(job.start_timestamp).format('h:mm A')} ~ {moment(job.end_timestamp).format('h:mm A')}
                    </Text>
                  )}
                </View>
              )}
            </View>

            {/* Notes Section */}
            {job.note && (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Additional Notes</Text>
                <Text style={styles.descriptionText}>{job.note}</Text>
              </View>
            )}

            {/* Cancel/Reject Actions */}
            {currentJob?.status === 1 && (
              <View style={styles.actionSection}>
                <TouchableOpacity onPress={handleReject}>
                  <Text style={styles.rejectText}>Reject this Job</Text>
                </TouchableOpacity>
              </View>
            )}

            {[2, 3].includes(currentJob?.status) && (
              <View style={styles.actionSection}>
                <TouchableOpacity
                  style={styles.cancelBtn}
                  onPress={() => navigation.navigate('CancelJobScreen', { currentJob })}
                >
                  <Text style={styles.cancelBtnText}>Cancel this Job</Text>
                </TouchableOpacity>
              </View>
            )}
          </View>

          {/* Sidebar */}
          <View style={[styles.sidebar, isMobile && styles.sidebarMobile]}>
            {/* Facility Card */}
            <View style={styles.facilityCard}>
              <Text style={styles.facilityCardTitle}>Facility Information</Text>
              <View style={styles.facilityInfo}>
                <View style={[styles.facilityLogo, { backgroundColor: getColorFromName(job.clinic?.name) }]}>
                  <Text style={styles.facilityLogoText}>{job.clinic?.name?.[0] || 'C'}</Text>
                </View>
                <View style={styles.facilityDetails}>
                  <Text style={styles.facilityName}>{job.clinic?.name}</Text>
                  <Text style={styles.facilityAddress}>{job.clinic?.address}</Text>
                  <Text style={styles.facilityAddress}>{job.clinic?.city}</Text>
                  <Text style={styles.facilityAddress}>{job.clinic?.postcode} {job.clinic?.state}</Text>
                </View>
              </View>

              {/* Show contact and map links when approved */}
              {isRecentlyApproved && (
                <>
                  {job.clinic?.phone_number && (
                    <TouchableOpacity
                      style={styles.contactBtn}
                      onPress={() => Linking.openURL(`tel:${job.clinic.phone_number}`)}
                    >
                      <View style={styles.contactBtnContent}>
                        <Phone size={14} color={webColors.textSecondary} />
                        <Text style={styles.contactBtnText}>{job.clinic.phone_number}</Text>
                      </View>
                    </TouchableOpacity>
                  )}
                  {job.contact_person ? (
                    <View style={styles.contactBtn}>
                      <View style={styles.contactBtnContent}>
                        <User size={14} color={webColors.textSecondary} />
                        <Text style={styles.contactBtnText}>{job.contact_person}</Text>
                      </View>
                    </View>
                  ) : null}
                  <View style={styles.mapButtons}>
                    <TouchableOpacity
                      style={styles.mapBtn}
                      onPress={() => Linking.openURL(`https://www.google.com/maps/search/?api=1&query=${job.clinic?.latitude},${job.clinic?.longitude}`)}
                    >
                      <View style={styles.mapBtnContent}>
                        <Navigation size={12} color={webColors.textSecondary} />
                        <Text style={styles.mapBtnText}>Google Maps</Text>
                      </View>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={styles.mapBtn}
                      onPress={() => Linking.openURL(`https://waze.com/ul?ll=${job.clinic?.latitude},${job.clinic?.longitude}&navigate=yes`)}
                    >
                      <View style={styles.mapBtnContent}>
                        <Navigation size={12} color={webColors.textSecondary} />
                        <Text style={styles.mapBtnText}>Waze</Text>
                      </View>
                    </TouchableOpacity>
                  </View>
                </>
              )}
            </View>

            {/* Job Summary Card */}
            <View style={styles.summaryCard}>
              <Text style={styles.summaryTitle}>Job Summary</Text>
              <View style={styles.summaryItem}>
                <Text style={styles.summaryLabel}>Job Type</Text>
                <Text style={styles.summaryValue}>
                  {job.international ? 'International' : jobTypes.find(type => type.id === parseInt(job.type))?.name || 'Locum'}
                </Text>
              </View>
              {!isFullTime && (
                <View style={styles.summaryItem}>
                  <Text style={styles.summaryLabel}>Rate</Text>
                  <Text style={styles.summaryValue}>
                    {formatPrice(job.price, displayCurrency)} / {diffHours}hr
                  </Text>
                </View>
              )}
              {isFullTime && job.price > 0 && (
                <View style={styles.summaryItem}>
                  <Text style={styles.summaryLabel}>Salary</Text>
                  <Text style={styles.summaryValue}>
                    {formatPriceRange(job.price, job.price_max, displayCurrency)}
                  </Text>
                </View>
              )}
              <View style={styles.summaryItem}>
                <Text style={styles.summaryLabel}>Location</Text>
                <Text style={styles.summaryValue}>{job.clinic?.city || '-'}</Text>
              </View>
              <View style={styles.summaryItem}>
                <Text style={styles.summaryLabel}>Payment</Text>
                <Text style={styles.summaryValue}>{job.is_cash ? 'Cash' : 'S-PAY'}</Text>
              </View>
              <View style={styles.summaryItem}>
                <Text style={styles.summaryLabel}>Posted</Text>
                <Text style={styles.summaryValue}>{moment(job.created_at).format('MMM D, YYYY')}</Text>
              </View>
            </View>

            {/* Check-in Button for approved locum jobs */}
            {isRecentlyApproved && !isFullTime && (
              <TouchableOpacity
                style={styles.checkinBtn}
                onPress={() => navigation.navigate('CheckinScreen')}
              >
                <View style={styles.checkinBtnContent}>
                  <Check size={16} color="#ffffff" />
                  <Text style={styles.checkinBtnText}>Check in Job</Text>
                </View>
              </TouchableOpacity>
            )}

            {/* Cash Payment Reminder */}
            {isRecentlyApproved && job.is_cash && (
              <View style={styles.reminderCard}>
                <View style={styles.reminderTitleRow}>
                  <AlertTriangle size={16} color="#92400e" />
                  <Text style={styles.reminderTitle}>Reminder</Text>
                </View>
                <Text style={styles.reminderText}>
                  This is a cash payment shift. You are to be paid the total amount stated for this shift. Please request payment directly from the facility.
                </Text>
              </View>
            )}
          </View>
        </View>
      </ScrollView>

      {/* Apply Modal */}
      <WebModal
        visible={showApplyModal}
        onClose={() => {
          setShowApplyModal(false);
          setApplySuccess(false);
        }}
        title={applySuccess ? 'Application Submitted!' : `Apply to ${job.title}`}
      >
        {applySuccess ? (
          <View style={styles.successContent}>
            <View style={styles.successIcon}>
              <Check size={28} color="#16a34a" />
            </View>
            <Text style={styles.successTitle}>
              {currentJob?.status === 1 ? 'Job Accepted!' : 'Application Submitted!'}
            </Text>
            <Text style={styles.successText}>
              {currentJob?.status === 1
                ? `You have accepted this job at ${job.clinic?.name}. Check your inbox for further instructions.`
                : `Your application has been sent to ${job.clinic?.name}. You'll be notified when they respond.`}
            </Text>
            <TouchableOpacity
              style={styles.successBtn}
              onPress={() => {
                setShowApplyModal(false);
                setApplySuccess(false);
                navigation.navigate('Home');
              }}
            >
              <Text style={styles.successBtnText}>Go to Home</Text>
            </TouchableOpacity>
          </View>
        ) : (
          <View style={styles.applyModalContent}>
            <Text style={styles.applyModalText}>
              {currentJob?.status === 1
                ? `Are you sure you want to accept this job at ${job.clinic?.name}?`
                : `Are you sure you want to apply for this job at ${job.clinic?.name}?`}
            </Text>
            <View style={styles.applyModalDetails}>
              <View style={styles.applyModalDetailRow}>
                <MapPin size={14} color={webColors.textSecondary} />
                <Text style={styles.applyModalDetail}>{job.clinic?.city}</Text>
              </View>
              <View style={styles.applyModalDetailRow}>
                <DollarSign size={14} color={webColors.textSecondary} />
                <Text style={styles.applyModalDetail}>{formatPrice(job.price, displayCurrency)}</Text>
              </View>
              <View style={styles.applyModalDetailRow}>
                <Calendar size={14} color={webColors.textSecondary} />
                <Text style={styles.applyModalDetail}>{moment(job.start_timestamp).format('MMM D, YYYY')}</Text>
              </View>
            </View>
            <View style={styles.applyModalActions}>
              <TouchableOpacity
                style={styles.cancelModalBtn}
                onPress={() => setShowApplyModal(false)}
              >
                <Text style={styles.cancelModalBtnText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.confirmModalBtn, applyMutation.isPending && styles.confirmModalBtnDisabled]}
                onPress={handleApply}
                disabled={applyMutation.isPending}
              >
                <Text style={styles.confirmModalBtnText}>
                  {applyMutation.isPending ? 'Processing...' : (currentJob?.status === 1 ? 'Accept Job' : 'Apply Now')}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        )}
      </WebModal>
    </WebLayout>
  );
};

// Helper function
const getColorFromName = (name) => {
  const colors = ['#fce7f3', '#dbeafe', '#dcfce7', '#fef3c7', '#f3e8ff'];
  return colors[name ? name.charCodeAt(0) % colors.length : 0];
};

const styles = StyleSheet.create({
  scrollContainer: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 100,
  },
  loadingText: {
    fontSize: 16,
    color: webColors.textMuted,
  },
  errorContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 100,
  },
  errorIcon: {
    fontSize: 48,
    fontWeight: '700',
    color: webColors.textMuted,
    marginBottom: 16,
  },
  errorTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: webColors.textPrimary,
    marginBottom: 8,
  },
  errorText: {
    fontSize: 14,
    color: webColors.textMuted,
    marginBottom: 24,
  },
  backBtn: {
    paddingHorizontal: 24,
    paddingVertical: 12,
    backgroundColor: webColors.primary,
    borderRadius: webBorderRadius.medium,
  },
  backBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#ffffff',
  },
  breadcrumb: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 24,
  },
  breadcrumbLink: {
    fontSize: 14,
    color: webColors.primary,
    fontWeight: '500',
  },
  breadcrumbSeparator: {
    fontSize: 14,
    color: webColors.textMuted,
  },
  breadcrumbCurrent: {
    fontSize: 14,
    color: webColors.textMuted,
  },
  contentLayout: {
    flexDirection: 'row',
    gap: 24,
  },
  mainContent: {
    flex: 1,
  },
  sidebar: {
    width: 340,
    flexShrink: 0,
  },
  jobHeaderCard: {
    backgroundColor: webColors.cardBackground,
    borderRadius: webBorderRadius.large,
    borderWidth: 1,
    borderColor: webColors.border,
    padding: 24,
    marginBottom: 24,
  },
  jobHeaderTop: {
    flexDirection: 'row',
    gap: 16,
    marginBottom: 20,
    paddingBottom: 20,
    borderBottomWidth: 1,
    borderBottomColor: webColors.borderLight,
  },
  companyLogo: {
    width: 56,
    height: 56,
    borderRadius: 28,
    alignItems: 'center',
    justifyContent: 'center',
  },
  companyLogoText: {
    fontSize: 20,
    fontWeight: '700',
    color: webColors.textPrimary,
  },
  jobHeaderInfo: {
    flex: 1,
  },
  urgentLabel: {
    fontSize: 12,
    fontWeight: '700',
    color: webColors.primary,
    marginBottom: 4,
  },
  companyName: {
    fontSize: 15,
    fontWeight: '600',
    color: webColors.primary,
    marginBottom: 8,
  },
  jobTitle: {
    fontSize: 28,
    fontWeight: '700',
    color: webColors.textPrimary,
    marginBottom: 12,
  },
  jobMeta: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
  },
  metaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  metaText: {
    fontSize: 14,
    color: webColors.textMuted,
  },
  headerActions: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 16,
  },
  applyBtn: {
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: webBorderRadius.medium,
    ...webShadows.button,
  },
  applyBtnText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#ffffff',
  },
  statusBadge: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: webBorderRadius.medium,
  },
  statusBadgeText: {
    fontSize: 15,
    fontWeight: '600',
  },
  saveBtn: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    backgroundColor: webColors.backgroundAlt,
    borderRadius: webBorderRadius.medium,
  },
  saveBtnContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  saveBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: webColors.textSecondary,
  },
  jobTags: {
    flexDirection: 'row',
    gap: 8,
    flexWrap: 'wrap',
  },
  jobTypeTag: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: webColors.primaryLight,
    borderRadius: webBorderRadius.small,
  },
  jobTypeTagText: {
    fontSize: 13,
    fontWeight: '600',
    color: webColors.primary,
  },
  spayTag: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: '#fee2e2',
    borderRadius: webBorderRadius.small,
  },
  spayTagText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#FC6767',
  },
  cashTag: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: '#f3f4f6',
    borderRadius: webBorderRadius.small,
  },
  cashTagText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#6b7280',
  },
  section: {
    backgroundColor: webColors.cardBackground,
    borderRadius: webBorderRadius.large,
    borderWidth: 1,
    borderColor: webColors.border,
    padding: 24,
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: webColors.textPrimary,
    marginBottom: 16,
  },
  descriptionText: {
    fontSize: 15,
    color: webColors.textSecondary,
    lineHeight: 24,
  },
  salaryText: {
    fontSize: 18,
    fontWeight: '600',
    color: webColors.textPrimary,
  },
  earningsHighlight: {
    fontSize: 20,
    fontWeight: '700',
    color: webColors.textPrimary,
    marginBottom: 8,
  },
  earningsTotal: {
    fontSize: 15,
    color: webColors.textSecondary,
    marginBottom: 12,
  },
  paymentTag: {
    alignSelf: 'flex-start',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderWidth: 1,
    borderRadius: webBorderRadius.small,
    marginBottom: 12,
  },
  paymentTagText: {
    fontSize: 12,
    fontWeight: '700',
  },
  paymentNote: {
    fontSize: 14,
    color: webColors.textMuted,
    lineHeight: 22,
  },
  expertiseGroup: {
    marginBottom: 16,
  },
  expertiseLabel: {
    fontSize: 12,
    fontWeight: '700',
    color: webColors.textSecondary,
    marginBottom: 8,
  },
  listItem: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 8,
  },
  listBullet: {
    fontSize: 16,
    color: '#5B9FF9',
  },
  listText: {
    flex: 1,
    fontSize: 15,
    color: webColors.textSecondary,
    lineHeight: 24,
  },
  dateRange: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  dateItem: {},
  dateMain: {
    fontSize: 16,
    fontWeight: '600',
    color: webColors.textPrimary,
    marginBottom: 4,
  },
  dateTime: {
    fontSize: 14,
    color: webColors.textSecondary,
  },
  dateSeparator: {
    fontSize: 18,
    color: webColors.textMuted,
  },
  actionSection: {
    marginBottom: 24,
  },
  rejectText: {
    fontSize: 14,
    color: '#ef4444',
  },
  cancelBtn: {
    paddingHorizontal: 24,
    paddingVertical: 12,
    backgroundColor: webColors.primary,
    borderRadius: webBorderRadius.medium,
    alignSelf: 'flex-start',
  },
  cancelBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#ffffff',
  },
  facilityCard: {
    backgroundColor: webColors.cardBackground,
    borderRadius: webBorderRadius.large,
    borderWidth: 1,
    borderColor: webColors.border,
    padding: 24,
    marginBottom: 16,
  },
  facilityCardTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: webColors.textPrimary,
    marginBottom: 16,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: webColors.borderLight,
  },
  facilityInfo: {
    flexDirection: 'row',
    gap: 16,
  },
  facilityLogo: {
    width: 56,
    height: 56,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  facilityLogoText: {
    fontSize: 20,
    fontWeight: '700',
    color: webColors.textPrimary,
  },
  facilityDetails: {
    flex: 1,
  },
  facilityName: {
    fontSize: 16,
    fontWeight: '600',
    color: webColors.textPrimary,
    marginBottom: 4,
  },
  facilityAddress: {
    fontSize: 14,
    color: webColors.textMuted,
    lineHeight: 20,
  },
  contactBtn: {
    marginTop: 16,
    paddingVertical: 10,
    backgroundColor: webColors.backgroundAlt,
    borderRadius: webBorderRadius.small,
    alignItems: 'center',
  },
  contactBtnContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  contactBtnText: {
    fontSize: 14,
    color: webColors.textSecondary,
  },
  mapButtons: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 8,
  },
  mapBtn: {
    flex: 1,
    paddingVertical: 10,
    backgroundColor: webColors.backgroundAlt,
    borderRadius: webBorderRadius.small,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: webColors.border,
  },
  mapBtnContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  mapBtnText: {
    fontSize: 12,
    fontWeight: '600',
    color: webColors.textSecondary,
  },
  summaryCard: {
    backgroundColor: webColors.cardBackground,
    borderRadius: webBorderRadius.large,
    borderWidth: 1,
    borderColor: webColors.border,
    padding: 24,
    marginBottom: 16,
  },
  summaryTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: webColors.textPrimary,
    marginBottom: 16,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: webColors.borderLight,
  },
  summaryItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: webColors.borderLight,
  },
  summaryLabel: {
    fontSize: 14,
    color: webColors.textMuted,
  },
  summaryValue: {
    fontSize: 14,
    fontWeight: '500',
    color: webColors.textPrimary,
    textAlign: 'right',
    maxWidth: '60%',
  },
  checkinBtn: {
    paddingVertical: 14,
    backgroundColor: '#FF4444',
    borderRadius: webBorderRadius.medium,
    alignItems: 'center',
    marginBottom: 16,
    ...webShadows.button,
  },
  checkinBtnContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  checkinBtnText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#ffffff',
  },
  reminderCard: {
    backgroundColor: '#fef3c7',
    borderRadius: webBorderRadius.medium,
    padding: 16,
    marginBottom: 16,
  },
  reminderTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
  },
  reminderTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: '#92400e',
  },
  reminderText: {
    fontSize: 13,
    color: '#92400e',
    lineHeight: 20,
  },
  successContent: {
    padding: 40,
    alignItems: 'center',
  },
  successIcon: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: '#dcfce7',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
  },
  successTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: webColors.textPrimary,
    marginBottom: 8,
  },
  successText: {
    fontSize: 14,
    color: webColors.textMuted,
    textAlign: 'center',
    marginBottom: 24,
  },
  successBtn: {
    paddingHorizontal: 24,
    paddingVertical: 12,
    backgroundColor: webColors.primary,
    borderRadius: webBorderRadius.medium,
  },
  successBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#ffffff',
  },
  applyModalContent: {
    padding: 24,
  },
  applyModalText: {
    fontSize: 16,
    color: webColors.textPrimary,
    marginBottom: 16,
    lineHeight: 24,
  },
  applyModalDetails: {
    padding: 16,
    backgroundColor: webColors.backgroundAlt,
    borderRadius: webBorderRadius.medium,
    marginBottom: 24,
    gap: 8,
  },
  applyModalDetailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  applyModalDetail: {
    fontSize: 14,
    color: webColors.textSecondary,
  },
  applyModalActions: {
    flexDirection: 'row',
    gap: 12,
    justifyContent: 'flex-end',
  },
  cancelModalBtn: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    backgroundColor: webColors.backgroundAlt,
    borderRadius: webBorderRadius.medium,
  },
  cancelModalBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: webColors.textSecondary,
  },
  confirmModalBtn: {
    paddingHorizontal: 24,
    paddingVertical: 12,
    backgroundColor: webColors.primary,
    borderRadius: webBorderRadius.medium,
    ...webShadows.button,
  },
  confirmModalBtnDisabled: {
    opacity: 0.6,
  },
  confirmModalBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#ffffff',
  },
  contentLayoutMobile: {
    flexDirection: 'column',
  },
  sidebarMobile: {
    width: '100%',
  },
  jobTitleMobile: {
    fontSize: 22,
  },
});

export default DetailsScreen;
